import React, { useState } from "react";
import { Button, message, Tooltip } from "antd";
import {
  UploadOutlined,
  DownloadOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import FileUploadModal from "./FileUploadModal";
import FileUpload from "./FileUpload";
import { useAuth } from "../hooks/useAuth";
import UpgradeWarningModal from "./UpgradeWarningModal";
import { downloadFromUrl } from "../utils/downloadUtils";

const isImageFile = (filename) => {
  const imageExtensions = [
    ".jpg",
    ".jpeg",
    ".png",
    ".gif",
    ".bmp",
    ".webp",
    ".heic",
    ".heif",
    ".svg",
  ];
  return imageExtensions.some((ext) => filename.toLowerCase().endsWith(ext));
};

const AttachmentsSection = ({
  projectId,
  value = [],
  onChange,
  disabled = false,
  label = "Add Attachments",
  title = "Upload Documents",
  description = "Upload your documents here. We accept various file formats.",
  accept = "*/*",
  maxSize = 262144000,
  multiple = true,
  disableAutoUpload = false,
}) => {
  const [isAttachmentModalOpen, setIsAttachmentModalOpen] = useState(false);
  const [isUpgradeModalVisible, setUpgradeModalVisible] = useState(false);

  const { selectedProject } = useAuth();

  const handleUploadSuccess = (files) => {
    const currentValue = Array.isArray(value) ? value : [];

    // For single file mode (like company logo), replace existing file
    if (!multiple) {
      onChange(files);
    } else {
      // For multiple file mode, add to existing files
      onChange([...currentValue, ...files]);
    }
  };

  const handleDeferredFileSelect = (files) => {
    const fileArray = Array.isArray(files) ? files : [files];
    onChange(fileArray);
  };

  const handleDownload = async (url, filename) => {
    try {
      await downloadFromUrl(url, filename);
    } catch (err) {
      message.error("Failed to download file.");
    }
  };

  const handleDelete = (fileId) => {
    const newFiles = value.filter((file) => file._id !== fileId);
    onChange(newFiles);
    message.success("File removed successfully");
  };

  return (
    <div className="w-full">
      {!disabled && (
        <>
          {disableAutoUpload ? (
            <div className="w-full mb-2">
              <FileUpload
                onUpload={handleDeferredFileSelect}
                accept={accept}
                maxSize={maxSize}
                multiple={multiple}
              />
            </div>
          ) : (
            <Button
              type="dashed"
              onClick={() =>
                selectedProject?.project_tier === 0
                  ? setUpgradeModalVisible(true)
                  : setIsAttachmentModalOpen(true)
              }
              className="w-full mb-2"
            >
              <UploadOutlined />{" "}
              {!multiple && value?.length > 0 ? "Replace Logo" : label}
            </Button>
          )}
        </>
      )}

      {value?.length > 0 && (
        <div className="mt-2">
          <div className="text-sm text-gray-600 mb-1">Attachments:</div>
          <ul className="list-none p-0 m-0 border rounded-lg divide-y">
            {value.map((file, index) => {
              const fileId =
                file._id ||
                (disableAutoUpload && file.name ? file.name : index);
              const fileName =
                file.filename ||
                (disableAutoUpload && file.name ? file.name : "Unknown file");
              const fileUrl =
                file.url ||
                (disableAutoUpload && file instanceof File
                  ? URL.createObjectURL(file)
                  : null);
              const isFileObject = disableAutoUpload && file instanceof File;

              return (
                <li
                  key={fileId}
                  className="p-2 flex items-center justify-between text-sm hover:bg-gray-50"
                >
                  <div className="flex items-center flex-1">
                    {isImageFile(fileName) && fileUrl && (
                      <div className="mr-3">
                        <img
                          src={fileUrl}
                          alt={fileName}
                          className="w-12 h-12 object-cover rounded"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src =
                              "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDgiIGhlaWdodD0iNDgiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0iI2YzZjRmNiIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iOCIgZmlsbD0iIzljYTNhZiIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkltYWdlPC90ZXh0Pjwvc3ZnPg==";
                          }}
                        />
                      </div>
                    )}
                    <div className="flex flex-col min-w-0">
                      {file.date && (
                        <span className="text-xs text-gray-500 mb-0.5">
                          {file.date}
                        </span>
                      )}
                      <Tooltip title={fileName} placement="topLeft">
                        <span
                          className="line-clamp-2 break-all max-w-xs"
                          style={{
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "normal",
                          }}
                        >
                          {fileName}
                        </span>
                      </Tooltip>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-2">
                    {fileUrl && !isFileObject && (
                      <Button
                        type="text"
                        size="small"
                        icon={<DownloadOutlined />}
                        onClick={() => handleDownload(fileUrl, fileName)}
                        title="Download file"
                      />
                    )}
                    {!disabled && (
                      <Button
                        type="text"
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => {
                          if (
                            disableAutoUpload &&
                            (isFileObject || !file._id)
                          ) {
                            const newFiles = value.filter(
                              (_, i) => i !== index,
                            );
                            onChange(newFiles);
                            message.success("File removed successfully");
                          } else {
                            handleDelete(file._id);
                          }
                        }}
                        title="Remove file"
                      />
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {!disableAutoUpload && (
        <FileUploadModal
          isOpen={isAttachmentModalOpen}
          onClose={() => setIsAttachmentModalOpen(false)}
          projectId={projectId}
          onUploadSuccess={handleUploadSuccess}
          title={title}
          description={description}
          accept={accept}
          maxSize={maxSize}
          multiple={multiple}
        />
      )}
      <UpgradeWarningModal
        visible={isUpgradeModalVisible}
        onClose={() => setUpgradeModalVisible(false)}
        text="You need to upgrade your membership to add attachments."
      />
    </div>
  );
};

export default AttachmentsSection;
